import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import alertService from '../../services/AlertService.js';
import Alert from './Alert.js';
import Button from './Button.js';
import { useAuth } from '../../context/AuthContext.js';

const SETTLEMENT_ALERT_REFS = new Set([
  'company_settlement',
  'paid_amount_settlement',
  'ml_agent_settlement',
  'ml_settlement',
  'pas_settlement',
  'paid_settlement',
]);

const settlementAlertPath = (alert, role) => {
  const type = String(alert?.reference_type || '').toLowerCase();
  const msg = String(alert?.message || '').toLowerCase();
  const isSettlement = SETTLEMENT_ALERT_REFS.has(type)
    || msg.includes('settlement approvals')
    || (msg.includes('settlement') && (msg.includes('review') || msg.includes('approved') || msg.includes('rejected')));
  if (!isSettlement) return null;
  if (role === 'Agent' || role === 'Hub Admin') return '/company-settlements';
  if (msg.includes('your company settlement')) return '/company-settlements';
  return '/company-settlement-approvals';
};

const alertNavigatePath = (alert, role) => {
  const settlement = settlementAlertPath(alert, role);
  if (settlement) return settlement;
  const type = String(alert?.reference_type || '').toLowerCase();
  if (type === 'itcl_request' && alert?.reference_id) {
    return `/ogcl-transfer-requests/view/${encodeURIComponent(alert.reference_id)}`;
  }
  if (type === 'ml_crossing_dispatch' && alert?.reference_id) {
    return `/ml-sheets/view/${encodeURIComponent(alert.reference_id)}`;
  }
  return null;
};

const AlertModal = ({ isOpen, onClose, userId, onUnreadChange }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('unread');
  const [category, setCategory] = useState('all');
  const [unreadAlerts, setUnreadAlerts] = useState([]);
  const [readAlerts, setReadAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  const openSettlementAlert = async (alert) => {
    const path = alertNavigatePath(alert, user?.role);
    if (!path) return;
    if (activeTab === 'unread' && alert?.id) {
      try {
        await alertService.markAsRead(alert.id);
      } catch {
        // still navigate
      }
    }
    onClose?.();
    navigate(path);
  };

  useEffect(() => {
    if (isOpen && userId) {
      fetchAlerts();
    }
  }, [isOpen, userId, activeTab]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const unreadResponse = await alertService.getUnread(userId);
      const unread = unreadResponse.data || [];
      setUnreadAlerts(unread);
      onUnreadChange?.(unread.length);

      if (activeTab === 'read') {
        const readResponse = await alertService.getRead(userId);
        setReadAlerts(readResponse.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
      Alert.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    const scoped = category !== 'all';
    const confirmed = await Alert.confirm(
      scoped ? 'Mark the shown alerts as read?' : 'Mark all unread alerts as read?',
      'Confirm Action',
      { theme: 'sky' }
    );
    if (confirmed) {
      try {
        if (scoped) {
          await Promise.all(visibleAlerts.map((alert) => alertService.markAsRead(alert.id)));
        } else {
          await alertService.markAllAsRead(userId);
        }
        Alert.success(scoped ? 'Shown alerts marked as read' : 'All alerts marked as read');
        fetchAlerts();
      } catch (err) {
        Alert.error('Failed to mark alerts as read');
      }
    }
  };

  const handleClearAllUnread = async () => {
    const scoped = category !== 'all';
    const confirmed = await Alert.confirm(
      scoped ? 'Delete the shown unread alerts permanently?' : 'Delete all unread alerts permanently?',
      'Confirm Delete',
      { theme: 'sky' }
    );
    if (confirmed) {
      try {
        if (scoped) {
          await Promise.all(visibleAlerts.map((alert) => alertService.deleteAlert(alert.id)));
        } else {
          await alertService.deleteAllUnread(userId);
        }
        Alert.success(scoped ? 'Shown unread alerts deleted' : 'All unread alerts deleted');
        fetchAlerts();
      } catch (err) {
        Alert.error('Failed to delete unread alerts');
      }
    }
  };

  const handleClearAllRead = async () => {
    const scoped = category !== 'all';
    const confirmed = await Alert.confirm(
      scoped ? 'Delete the shown read alerts permanently?' : 'Delete all read alerts permanently?',
      'Confirm Delete',
      { theme: 'sky' }
    );
    if (confirmed) {
      try {
        if (scoped) {
          await Promise.all(visibleAlerts.map((alert) => alertService.deleteAlert(alert.id)));
        } else {
          await alertService.deleteAllRead(userId);
        }
        Alert.success(scoped ? 'Shown read alerts deleted' : 'All read alerts deleted');
        fetchAlerts();
      } catch (err) {
        Alert.error('Failed to delete read alerts');
      }
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await alertService.deleteAlert(alertId);
      Alert.success('Alert deleted');
      fetchAlerts();
    } catch (err) {
      Alert.error('Failed to delete alert');
    }
  };

  const handleMarkAsRead = async (alertId) => {
    try {
      await alertService.markAsRead(alertId);
      fetchAlerts();
    } catch (err) {
      Alert.error('Failed to mark alert as read');
    }
  };

  const getAlertTypeColor = (type) => {
    const colors = {
      info: 'bg-blue-50 border-blue-200 text-blue-800',
      warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      error: 'bg-red-50 border-red-200 text-red-800',
      success: 'bg-green-50 border-green-200 text-green-800'
    };
    return colors[type] || colors.info;
  };

  const getAlertTypeIcon = (type) => {
    const icons = {
      info: (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      ),
      warning: (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      ),
      error: (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      ),
      success: (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      )
    };
    return icons[type] || icons.info;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  if (!isOpen) return null;

  const categoryLabel = (type) => {
    const key = String(type || '').toLowerCase();
    const names = {
      'daily-ops': 'Ops',
      'daily-outstanding': 'Outstanding',
      'daily-tat': 'Delivery',
      'daily-outstanding-email': 'Outstanding',
    };
    if (names[key]) return names[key];
    return key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
  };

  const currentAlerts = activeTab === 'unread' ? unreadAlerts : readAlerts;
  const categories = [];
  currentAlerts.forEach((alert) => {
    const key = String(alert.reference_type || 'other');
    const found = categories.find((item) => item.key === key);
    if (found) found.count += 1;
    else categories.push({ key, label: categoryLabel(key), count: 1 });
  });
  const activeCategory = categories.some((item) => item.key === category) ? category : 'all';
  const visibleAlerts = activeCategory === 'all'
    ? currentAlerts
    : currentAlerts.filter((alert) => String(alert.reference_type || 'other') === activeCategory);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div 
        className="bg-white w-full sm:max-w-2xl sm:rounded-lg shadow-xl h-[85vh] sm:h-auto sm:max-h-[90vh] flex flex-col overflow-hidden rounded-t-3xl sm:rounded-b-lg border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile Pull Indicator */}
        <div className="flex sm:hidden justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-sky-300 rounded-full"></div>
        </div>

        {/* Header */}
        <div className="px-3 sm:px-4 py-3 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-3xl sm:rounded-t-lg">
          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2">
            <h2 className="text-sm sm:text-lg font-semibold text-sky-950">Notifications</h2>
            <div className="flex items-center justify-center gap-1.5 sm:gap-2 min-w-0">
              {visibleAlerts.length > 0 && activeTab === 'unread' && (
                <>
                  <button
                    type="button"
                    onClick={handleMarkAllAsRead}
                    className="px-2 py-1 rounded-lg border border-blue-200 bg-blue-50 text-[10px] sm:text-xs font-semibold text-blue-700 hover:bg-blue-100 whitespace-nowrap"
                  >
                    Mark all as read
                  </button>
                  <button
                    type="button"
                    onClick={handleClearAllUnread}
                    className="px-2 py-1 rounded-lg border border-red-200 bg-red-50 text-[10px] sm:text-xs font-semibold text-red-700 hover:bg-red-100 whitespace-nowrap"
                  >
                    Clear all unread
                  </button>
                </>
              )}
              {visibleAlerts.length > 0 && activeTab === 'read' && (
                <button
                  type="button"
                  onClick={handleClearAllRead}
                  className="px-2 py-1 rounded-lg border border-red-200 bg-red-50 text-[10px] sm:text-xs font-semibold text-red-700 hover:bg-red-100 whitespace-nowrap"
                >
                  Clear all read
                </button>
              )}
            </div>
            <Button
              variant="ghost"
              onClick={onClose}
              className="p-1.5 sm:p-2 rounded-full touch-manipulation hover:bg-sky-100"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-sky-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-white">
          <Button
            variant="ghost"
            onClick={() => setActiveTab('unread')}
            className={`flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm rounded-none touch-manipulation ${
              activeTab === 'unread'
                ? 'text-sky-700 border-b-2 border-sky-600 bg-white'
                : 'text-gray-600 hover:text-sky-800 hover:bg-gray-50'
            }`}
          >
            <span className="hidden sm:inline">Unread</span>
            <span className="sm:hidden">Unread</span>
            {unreadAlerts.length > 0 && (
              <span className={`ml-1.5 px-1.5 py-0.5 text-[10px] sm:text-xs font-bold rounded-full ${
                activeTab === 'unread' 
                  ? 'bg-sky-600 text-white' 
                  : 'bg-sky-100 text-sky-800'
              }`}>
                {unreadAlerts.length}
              </span>
            )}
          </Button>
          <Button
            variant="ghost"
            onClick={() => setActiveTab('read')}
            className={`flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm rounded-none touch-manipulation ${
              activeTab === 'read'
                ? 'text-sky-700 border-b-2 border-sky-600 bg-white'
                : 'text-gray-600 hover:text-sky-800 hover:bg-gray-50'
            }`}
          >
            <span className="hidden sm:inline">Read</span>
            <span className="sm:hidden">Read</span>
            {readAlerts.length > 0 && (
              <span className={`ml-1.5 px-1.5 py-0.5 text-[10px] sm:text-xs font-bold rounded-full ${
                activeTab === 'read' 
                  ? 'bg-sky-600 text-white' 
                  : 'bg-sky-100 text-sky-800'
              }`}>
                {readAlerts.length}
              </span>
            )}
          </Button>
        </div>

        {/* Action Buttons */}
        {categories.length > 1 && (
          <div className="relative z-20 w-full shrink-0 grow-0 border-b border-gray-200 bg-white">
          <div className="chip-scroll flex h-11 w-full items-center gap-1.5 overflow-x-auto overflow-y-hidden px-3 sm:px-4">
            <button
              type="button"
              onClick={() => setCategory('all')}
              className={`shrink-0 box-border inline-flex h-7 items-center gap-1 whitespace-nowrap rounded-full px-2.5 text-[11px] font-medium leading-none transition-colors outline-none ${
                activeCategory === 'all'
                  ? 'bg-sky-600 text-white hover:bg-sky-700'
                  : 'bg-sky-50 text-sky-800 hover:bg-sky-100 hover:text-sky-950'
              }`}
            >
              All
              <span className={`box-border inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none tabular-nums ${
                activeCategory === 'all' ? 'bg-white text-sky-700' : 'bg-sky-600 text-white'
              }`}>
                {currentAlerts.length}
              </span>
            </button>
            {categories.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setCategory(item.key)}
                className={`shrink-0 box-border inline-flex h-7 items-center gap-1 whitespace-nowrap rounded-full px-2.5 text-[11px] font-medium leading-none transition-colors outline-none ${
                  activeCategory === item.key
                    ? 'bg-sky-600 text-white hover:bg-sky-700'
                    : 'bg-sky-50 text-sky-800 hover:bg-sky-100 hover:text-sky-950'
                }`}
              >
                {item.label}
                <span className={`box-border inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none tabular-nums ${
                  activeCategory === item.key ? 'bg-white text-sky-700' : 'bg-sky-600 text-white'
                }`}>
                  {item.count}
                </span>
              </button>
            ))}
          </div>
          </div>
        )}

        {/* Alert List */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {loading ? (
            <div className="flex items-center justify-center py-16 sm:py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-sky-500 mx-auto mb-4"></div>
                <p className="text-sky-800 text-xs sm:text-sm">Loading notifications...</p>
              </div>
            </div>
          ) : visibleAlerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 sm:py-20 px-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-sky-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 sm:w-10 sm:h-10 text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <p className="text-sky-950 text-sm sm:text-base font-semibold mb-1">
                {activeCategory === 'all' ? `No ${activeTab} alerts` : 'No alerts in this category'}
              </p>
              <p className="text-sky-700/70 text-xs sm:text-sm">You're all caught up!</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 bg-white">
              {visibleAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`bg-white p-3 sm:p-4 hover:bg-gray-50 transition-colors active:bg-gray-100 ${
                    alertNavigatePath(alert, user?.role) ? 'cursor-pointer' : ''
                  }`}
                  onClick={() => openSettlementAlert(alert)}
                >
                  <div className="flex gap-2.5 sm:gap-3">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${getAlertTypeColor(alert.alert_type)}`}>
                      {getAlertTypeIcon(alert.alert_type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1.5 sm:mb-2">
                        <p className="text-xs sm:text-sm text-gray-900 break-words whitespace-pre-wrap leading-relaxed pr-2">
                          {alert.message}
                        </p>
                        <Button
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAlert(alert.id);
                          }}
                          className="flex-shrink-0 p-1.5 sm:p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full touch-manipulation active:bg-red-100"
                          title="Delete alert"
                        >
                          <svg className="w-4 h-4 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </Button>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-500">
                        <span className="font-medium">{formatDate(alert.created_at)}</span>
                        {alert.reference_type && (
                          <>
                            <span className="hidden sm:inline">•</span>
                            <span className="capitalize bg-gray-100 px-2 py-0.5 rounded-full text-[10px]">
                              {alert.reference_type}
                            </span>
                          </>
                        )}
                        {activeTab === 'unread' && (
                          <>
                            <span className="hidden sm:inline">•</span>
                            <Button
                              variant="link"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsRead(alert.id);
                              }}
                              className="hover:text-blue-700 touch-manipulation active:text-blue-800"
                            >
                              Mark as read
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Mobile Bottom Safe Area */}
        <div className="h-safe-area-inset-bottom sm:hidden bg-white"></div>
      </div>
    </div>
  );
};

export default AlertModal;